import React, { useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useT } from "@transifex/react";
import { RendererProps } from "./index";
import { FieldLabel, PillGroup, Pill, HeightSection, TextInput } from "./renderer-styles";

const CM_PER_INCH = 2.54;
const HEIGHT_PATTERN = /^(\d+)\s*ft\s*(?:(\d+)\s*in)?$/i;

type Unit = "imperial" | "metric";

const ToggleRow = styled.div`
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
  width: fit-content;
`;

const ToggleButton = styled.button<{ $active?: boolean }>`
  padding: 6px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  font-family: "Dosis", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ $active }) => ($active ? "white" : "transparent")};
  color: ${({ $active }) => ($active ? "#0066cc" : "#64748b")};
  box-shadow: ${({ $active }) => ($active ? "0 1px 3px rgba(0,0,0,0.1)" : "none")};
`;

const MetricInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricLabel = styled.span`
  font-size: 16px;
  color: #4a4d5e;
  font-weight: 600;
`;

function parseHeight(label: string): { feet: number; inches: number } | null {
  const match = label.match(HEIGHT_PATTERN);
  if (!match) return null;
  return { feet: parseInt(match[1], 10), inches: parseInt(match[2] || "0", 10) };
}

function totalInchesFromChoice(label: string): number | null {
  const p = parseHeight(label);
  return p ? p.feet * 12 + p.inches : null;
}

function buildChoiceLookup(
  choices: { title?: string; label?: string; value: string }[]
): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const c of choices) {
    const parsed = parseHeight((c.title || c.label) ?? "");
    if (parsed) lookup.set(`${parsed.feet}-${parsed.inches}`, c.value);
  }
  return lookup;
}

function buildInchesLookup(
  choices: { title?: string; label?: string; value: string }[]
): Map<number, string> {
  const lookup = new Map<number, string>();
  for (const c of choices) {
    const inches = totalInchesFromChoice((c.title || c.label) ?? "");
    if (inches != null) lookup.set(inches, c.value);
  }
  return lookup;
}

function findClosestInches(target: number, available: number[]): number | null {
  if (available.length === 0) return null;
  let best = available[0];
  let bestDiff = Math.abs(target - best);
  for (const v of available) {
    const diff = Math.abs(target - v);
    if (diff < bestDiff) {
      best = v;
      bestDiff = diff;
    }
  }
  return best;
}

const HeightSelectRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {
  const t = useT();
  const choices = question.choices || question.options || [];
  const lookup = useMemo(() => buildChoiceLookup(choices), [choices]);
  const inchesLookup = useMemo(() => buildInchesLookup(choices), [choices]);
  const allInches = useMemo(
    () => Array.from(inchesLookup.keys()).sort((a, b) => a - b),
    [inchesLookup]
  );

  const [unit, setUnit] = useState<Unit>("imperial");

  const feetRange = useMemo(() => {
    const feetSet = new Set<number>();
    for (const c of choices) {
      const parsed = parseHeight((c.title || c.label) ?? "");
      if (parsed) feetSet.add(parsed.feet);
    }
    return Array.from(feetSet).sort((a, b) => a - b);
  }, [choices]);

  const initialParsed = useMemo(() => {
    if (!answer?.value) return null;
    const match = choices.find((c) => c.value === answer.value);
    if (!match) return null;
    return parseHeight((match.title || match.label) ?? "");
  }, [answer?.value, choices]);

  const initialTotalInches = useMemo(() => {
    if (!initialParsed) return null;
    return initialParsed.feet * 12 + initialParsed.inches;
  }, [initialParsed]);

  const [feet, setFeet] = useState<number | null>(initialParsed?.feet ?? null);
  const [inches, setInches] = useState<number | null>(initialParsed?.inches ?? null);
  const [cmInput, setCmInput] = useState<string>(
    initialTotalInches != null ? String(Math.round(initialTotalInches * CM_PER_INCH)) : ""
  );

  const emitImperial = useCallback(
    (ft: number | null, inc: number | null, autoAdvanceReady?: boolean) => {
      if (ft === null) {
        onAnswer("", false);
        return;
      }
      const key = `${ft}-${inc ?? 0}`;
      const choiceValue = lookup.get(key);
      if (choiceValue !== undefined) {
        const totalIn = ft * 12 + (inc ?? 0);
        setCmInput(String(Math.round(totalIn * CM_PER_INCH)));
        onAnswer(choiceValue, true, undefined, autoAdvanceReady);
      } else {
        onAnswer("", false);
      }
    },
    [lookup, onAnswer]
  );

  const minCm = allInches.length > 0 ? Math.round(allInches[0] * CM_PER_INCH) : 0;
  const maxCm = allInches.length > 0 ? Math.round(allInches[allInches.length - 1] * CM_PER_INCH) : 300;

  const emitMetric = useCallback(
    (cmStr: string) => {
      setCmInput(cmStr);
      if (!cmStr) {
        setFeet(null);
        setInches(null);
        onAnswer("", false);
        return;
      }
      const cm = Number(cmStr);
      if (isNaN(cm) || cm < minCm || cm > maxCm) {
        setFeet(null);
        setInches(null);
        onAnswer("", false);
        return;
      }
      const totalIn = cm / CM_PER_INCH;
      const closest = findClosestInches(Math.round(totalIn), allInches);
      if (closest != null) {
        const ft = Math.floor(closest / 12);
        const inc = closest % 12;
        setFeet(ft);
        setInches(inc);
        const choiceValue = inchesLookup.get(closest);
        if (choiceValue !== undefined) {
          onAnswer(choiceValue, true, undefined, false);
          return;
        }
      }
      setFeet(null);
      setInches(null);
      onAnswer("", false);
    },
    [allInches, inchesLookup, onAnswer, minCm, maxCm]
  );

  const handleFeetSelect = useCallback(
    (ft: number) => {
      setFeet(ft);
      const defaultInches = lookup.has(`${ft}-0`) ? 0 : null;
      setInches(defaultInches);
      emitImperial(ft, defaultInches, false);
    },
    [lookup, emitImperial]
  );

  const handleInchesSelect = useCallback(
    (inc: number) => {
      setInches(inc);
      emitImperial(feet, inc, true);
    },
    [feet, emitImperial]
  );

  const availableInches = useMemo(() => {
    if (feet === null) return [];
    const result: number[] = [];
    for (let i = 0; i <= 11; i++) {
      if (lookup.has(`${feet}-${i}`)) result.push(i);
    }
    return result;
  }, [feet, lookup]);

  const handleUnitToggle = useCallback(
    (newUnit: Unit) => {
      setUnit(newUnit);
    },
    []
  );

  return (
    <HeightSection>
      <ToggleRow>
        <ToggleButton
          type="button"
          $active={unit === "imperial"}
          onClick={() => handleUnitToggle("imperial")}
        >
          {t("ft/in")}
        </ToggleButton>
        <ToggleButton
          type="button"
          $active={unit === "metric"}
          onClick={() => handleUnitToggle("metric")}
        >
          {t("cm")}
        </ToggleButton>
      </ToggleRow>

      {unit === "imperial" ? (
        <>
          <div>
            <FieldLabel>{t("Feet")}</FieldLabel>
            <PillGroup>
              {feetRange.map((ft) => (
                <Pill
                  key={ft}
                  type="button"
                  selected={feet === ft}
                  onClick={() => handleFeetSelect(ft)}
                >
                  {ft}&prime;
                </Pill>
              ))}
            </PillGroup>
          </div>
          {feet !== null && availableInches.length > 0 && (
            <div>
              <FieldLabel>{t("Inches")}</FieldLabel>
              <PillGroup>
                {availableInches.map((inc) => (
                  <Pill
                    key={inc}
                    type="button"
                    selected={inches === inc}
                    onClick={() => handleInchesSelect(inc)}
                  >
                    {inc}&Prime;
                  </Pill>
                ))}
              </PillGroup>
            </div>
          )}
        </>
      ) : (
        <MetricInputRow>
          <TextInput
            type="number"
            value={cmInput}
            onChange={(e) => emitMetric(e.target.value)}
            placeholder={`${minCm}-${maxCm}`}
            min={minCm}
            max={maxCm}
            style={{ maxWidth: 120 }}
          />
          <MetricLabel>cm</MetricLabel>
        </MetricInputRow>
      )}
    </HeightSection>
  );
};

export default HeightSelectRenderer;
